import React, { useState } from "react";
import { Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { useSnackbar } from "notistack";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningItem, SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { useApi } from "../../../../useApi";
import { useConfirmDialog } from "../../../../../hooks/useConfirmDialog";
import { formatValue, humanizeKey } from "./DynamicFieldsList";
import { EditBoxDialog } from "./EditBoxDialog";
import { EditSensorDialog } from "./EditSensorDialog";
import { EditIoDeviceDialog } from "./EditIoDeviceDialog";
import { removeBox, removeIoDevice, removeSensor, splitTopologyItems } from "./siteTopologyEditing";
import { humanizeDeviceType } from "./ioDeviceLabels";

function sensorSerialNumber(key: string): string {
    return key.substring(key.lastIndexOf(":") + 1);
}

const useStyles = makeStyles((theme: Theme) => ({
    sectionHead: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "20px",
        marginBottom: "8px",
    },
    subheading: {
        color: theme.palette.text.primary,
        fontWeight: 600,
    },
    tableHeadCell: {
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontSize: "10.5px",
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    nameCell: {
        fontWeight: 500,
    },
    actionsCell: {
        whiteSpace: "nowrap",
        width: "1%",
    },
}));

interface Entry {
    key: string;
    displayName: string;
    fields: Record<string, unknown>;
}

function buildColumns(entries: Entry[]): string[] {
    const seen = new Set<string>();
    const columns: string[] = [];
    entries.forEach(({ fields }) => {
        Object.keys(fields).forEach((key) => {
            if (!seen.has(key)) {
                seen.add(key);
                columns.push(key);
            }
        });
    });
    return columns;
}

interface DynamicTableProps {
    nameColumnLabel: string;
    entries: Entry[];
    renderCell?: (column: string, value: unknown) => string | undefined;
    onEdit?: (key: string) => void;
    onDelete?: (key: string) => void;
    deleteDisabledReason?: (key: string) => string | undefined;
}

const DynamicTable: React.FC<DynamicTableProps> = ({ nameColumnLabel, entries, renderCell, onEdit, onDelete, deleteDisabledReason }) => {
    const classes = useStyles();
    const columns = buildColumns(entries);
    const showActions = Boolean(onEdit || onDelete);

    return (
        <div style={{ overflowX: "auto" }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.tableHeadCell}>{nameColumnLabel}</TableCell>
                        {columns.map((column) => (
                            <TableCell className={classes.tableHeadCell} key={column}>{humanizeKey(column)}</TableCell>
                        ))}
                        {showActions && <TableCell className={classes.tableHeadCell} />}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map((entry) => (
                        <TableRow key={entry.key}>
                            <TableCell className={classes.nameCell}>{entry.displayName}</TableCell>
                            {columns.map((column) => (
                                <TableCell key={column}>
                                    {renderCell?.(column, entry.fields[column]) ?? formatValue(entry.fields[column])}
                                </TableCell>
                            ))}
                            {showActions && (
                                <TableCell className={classes.actionsCell}>
                                    {onEdit && (
                                        <IconButton size="small" onClick={() => onEdit(entry.key)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    {onDelete && (() => {
                                        const disabledReason = deleteDisabledReason?.(entry.key);
                                        const deleteButton = (
                                            <IconButton
                                                size="small"
                                                onClick={() => onDelete(entry.key)}
                                                disabled={Boolean(disabledReason)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        );
                                        return disabledReason ? (
                                            <Tooltip title={disabledReason}>
                                                <span>{deleteButton}</span>
                                            </Tooltip>
                                        ) : deleteButton;
                                    })()}
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

interface Props {
    items: Record<string, SiteCommissioningItem>;
    otherItems: Record<string, SiteCommissioningItem>;
    scope: "boxes" | "ioDevices";
    sectionStatus?: string | null;
    siteId?: number;
    canEdit?: boolean;
    onSummaryUpdated?: (summary: SiteCommissioningSummary) => void;
}

type EditingTarget = { type: "box" | "sensor" | "ioDevice"; key?: string } | null;

export const SiteTopologyView: React.FC<Props> = ({ items, otherItems, scope, sectionStatus, siteId, canEdit = false, onSummaryUpdated }) => {
    const classes = useStyles();
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { showConfirmDialog } = useConfirmDialog();
    const [editing, setEditing] = useState<EditingTarget>(null);

    const allItems = { ...items, ...otherItems };

    const boxEntries = Object.entries(allItems).filter(([key]) => key.startsWith("box:"));
    const sensorEntries = Object.entries(allItems).filter(([key]) => key.startsWith("sensor:"));
    const ioDeviceEntries = Object.entries(allItems).filter(([key]) => key.startsWith("ioDevice:"));

    const sensorNameByKey = Object.fromEntries(
        sensorEntries.map(([key, item]) => [key, (item.fields.name as string) ?? key])
    );

    const submitItems = (newItems: Record<string, SiteCommissioningItem>, successMessage: string) => {
        if (!siteId) {
            return;
        }
        const { ioDeviceItems, boxItems } = splitTopologyItems(newItems);
        siteApi.updateCommissioning(siteId, [
            { name: "ioDevices", items: ioDeviceItems },
            { name: "boxes", items: boxItems },
        ]).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                onSummaryUpdated?.(r.data);
                enqueueSnackbar(successMessage, { variant: "success" });
            } else {
                enqueueSnackbar("ERROR: Failed to update site topology", { variant: "error" });
            }
        });
    };

    const deleteBox = (key: string) => {
        showConfirmDialog(`Remove box ${key.substring("box:".length)}?`).then((confirmed) => {
            if (confirmed) {
                submitItems(removeBox(allItems, key), "Box removed");
            }
        });
    };

    const deleteSensor = (key: string) => {
        showConfirmDialog(`Remove sensor ${sensorNameByKey[key] ?? key}?`).then((confirmed) => {
            if (confirmed) {
                submitItems(removeSensor(allItems, key), "Sensor removed");
            }
        });
    };

    const ioDeviceDeleteDisabledReason = (key: string): string | undefined => {
        const sensorPrefix = `sensor:${key}:`;
        if (sensorEntries.some(([sensorKey]) => sensorKey.startsWith(sensorPrefix))) {
            return "Remove this device's sensors first";
        }
        if (sectionStatus !== "Applied") {
            return "Wait for the I/O Devices section to finish syncing before removing a device";
        }
        return undefined;
    };

    const deleteIoDevice = (key: string) => {
        showConfirmDialog("Remove this I/O device?").then((confirmed) => {
            if (confirmed) {
                submitItems(removeIoDevice(allItems, key), "I/O device removed");
            }
        });
    };

    const addableIoDeviceTypes = ["dmp", "pz520relay", "pz551wirelessreceiver"];
    const presentIoDeviceTypes = new Set(ioDeviceEntries.map(([key]) => key.split(":")[1]));
    const allIoDeviceTypesPresent = addableIoDeviceTypes.every((type) => presentIoDeviceTypes.has(type));

    const sensorCapableIoDeviceKeys = ioDeviceEntries.filter(([key]) => key.split(":")[1] !== "pz520relay").map(([key]) => key);

    const showIoSection = scope === "ioDevices" && (canEdit || ioDeviceEntries.length > 0);
    const showSensorSection = scope === "ioDevices" && (canEdit || sensorEntries.length > 0);
    const showBoxSection = scope === "boxes" && (canEdit || boxEntries.length > 0);

    if (scope === "ioDevices" && !showIoSection && !showSensorSection) {
        return <Typography variant="body2">No I/O devices configured.</Typography>;
    }
    if (scope === "boxes" && !showBoxSection) {
        return <Typography variant="body2">No boxes configured.</Typography>;
    }

    return (
        <>
            {showIoSection && (
            <>
            <div className={classes.sectionHead}>
                <Typography variant="subtitle2" className={classes.subheading}>IO Devices</Typography>
                {canEdit && siteId && (
                    <Button
                        size="small"
                        onClick={() => setEditing({ type: "ioDevice" })}
                        disabled={allIoDeviceTypesPresent}
                    >
                        <AddIcon fontSize="small" style={{ marginRight: "4px" }} />
                        Add
                    </Button>
                )}
            </div>
            {ioDeviceEntries.length > 0 ? (
                <DynamicTable
                    nameColumnLabel="Device"
                    entries={ioDeviceEntries.map(([key, item]) => ({
                        key,
                        displayName: humanizeDeviceType(key.split(":")[1]),
                        fields: item.fields,
                    }))}
                    onEdit={canEdit && siteId ? (key) => setEditing({ type: "ioDevice", key }) : undefined}
                    onDelete={canEdit && siteId ? deleteIoDevice : undefined}
                    deleteDisabledReason={canEdit && siteId ? ioDeviceDeleteDisabledReason : undefined}
                />
            ) : (
                <Typography variant="body2">No I/O device configured.</Typography>
            )}
            </>
            )}

            {showSensorSection && (
            <>
            <div className={classes.sectionHead}>
                <Typography variant="subtitle2" className={classes.subheading}>Sensors</Typography>
                {canEdit && siteId && (
                    <Button
                        size="small"
                        onClick={() => setEditing({ type: "sensor" })}
                        disabled={sensorCapableIoDeviceKeys.length === 0}
                        title={ioDeviceEntries.length === 0 ? "Add an I/O device first" : undefined}
                    >
                        <AddIcon fontSize="small" style={{ marginRight: "4px" }} />
                        Add
                    </Button>
                )}
            </div>
            {sensorEntries.length > 0 ? (
                <DynamicTable
                    nameColumnLabel="Serial Number"
                    entries={sensorEntries.map(([key, item]) => {
                        const { invertSerialNumber, ...displayFields } = item.fields;
                        return { key, displayName: sensorSerialNumber(key), fields: displayFields };
                    })}
                    onEdit={canEdit && siteId ? (key) => setEditing({ type: "sensor", key }) : undefined}
                    onDelete={canEdit && siteId ? deleteSensor : undefined}
                />
            ) : (
                <Typography variant="body2">No sensors configured.</Typography>
            )}
            </>
            )}

            {showBoxSection && (
            <>
            <div className={classes.sectionHead}>
                <Typography variant="subtitle2" className={classes.subheading}>Boxes</Typography>
                {canEdit && siteId && (
                    <Button size="small" onClick={() => setEditing({ type: "box" })}>
                        <AddIcon fontSize="small" style={{ marginRight: "4px" }} />
                        Add
                    </Button>
                )}
            </div>
            {boxEntries.length > 0 ? (
                <DynamicTable
                    nameColumnLabel="Box"
                    entries={boxEntries.map(([key, item]) => ({ key, displayName: key.split(":")[1], fields: item.fields }))}
                    renderCell={(column, value) =>
                        column === "sensors" && Array.isArray(value)
                            ? (value.length > 0
                                ? value.map((sensorKey) => sensorNameByKey[sensorKey as string] ?? sensorKey).join(", ")
                                : "—")
                            : undefined
                    }
                    onEdit={canEdit && siteId ? (key) => setEditing({ type: "box", key }) : undefined}
                    onDelete={canEdit && siteId ? deleteBox : undefined}
                />
            ) : (
                <Typography variant="body2">No boxes configured.</Typography>
            )}
            </>
            )}

            {canEdit && siteId && scope === "ioDevices" && (
                <>
                    <EditIoDeviceDialog
                        open={editing?.type === "ioDevice"}
                        onClose={() => setEditing(null)}
                        siteId={siteId}
                        existingItems={allItems}
                        editingKey={editing?.type === "ioDevice" ? editing.key : undefined}
                        onSaved={(s) => onSummaryUpdated?.(s)}
                    />
                    <EditSensorDialog
                        open={editing?.type === "sensor"}
                        onClose={() => setEditing(null)}
                        siteId={siteId}
                        existingItems={allItems}
                        ioDeviceKeys={sensorCapableIoDeviceKeys}
                        editingKey={editing?.type === "sensor" ? editing.key : undefined}
                        onSaved={(s) => onSummaryUpdated?.(s)}
                    />
                </>
            )}
            {canEdit && siteId && scope === "boxes" && (
                <EditBoxDialog
                    open={editing?.type === "box"}
                    onClose={() => setEditing(null)}
                    siteId={siteId}
                    existingItems={allItems}
                    sensorOptions={sensorEntries.map(([key]) => ({ key, name: sensorNameByKey[key] }))}
                    editingKey={editing?.type === "box" ? editing.key : undefined}
                    onSaved={(s) => onSummaryUpdated?.(s)}
                />
            )}
        </>
    );
};
